import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

const DESKTOP_QUERY = "(min-width: 768px)";
const PREFERENCE_VERSION = 1;

type PersistedPreference = {
  version: 1;
  collapsed: boolean;
};

function subscribeToDesktopBreakpoint(onChange: () => void) {
  const query = window.matchMedia(DESKTOP_QUERY);
  const listener = () => onChange();
  if (typeof query.addEventListener === "function") {
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }
  query.addListener(listener);
  return () => query.removeListener(listener);
}

function isDesktopViewport() {
  return window.matchMedia(DESKTOP_QUERY).matches;
}

function parsePreference(raw: string | null): boolean | null {
  if (raw === null) return null;
  try {
    const value = JSON.parse(raw) as Partial<PersistedPreference> | null;
    return value?.version === PREFERENCE_VERSION &&
      typeof value.collapsed === "boolean"
      ? value.collapsed
      : null;
  } catch {
    return null;
  }
}

export function sidebarPreferenceKey(tenantSlug: string, preset: string) {
  return `hrw.sidebar.preference.v1:${encodeURIComponent(tenantSlug)}:${encodeURIComponent(preset)}`;
}

function legacyPreferenceKeys(tenantSlug: string, preset: string) {
  return [
    `hrw.sidebar.collapsed:${tenantSlug}:${preset}`,
    `hrw.sidebar.collapsed:${window.location.host}:${preset}`,
  ].filter((key, index, keys) => keys.indexOf(key) === index);
}

function readPreference(
  storageKey: string,
  legacyKeys: ReadonlyArray<string>,
): boolean | null {
  try {
    const current = window.localStorage.getItem(storageKey);
    if (current !== null) return parsePreference(current);
    for (const legacyKey of legacyKeys) {
      const legacy = window.localStorage.getItem(legacyKey);
      if (legacy === "true") return true;
      if (legacy === "false") return false;
    }
  } catch {
    // Presentation state is allowed to fall back when storage is unavailable.
  }
  return null;
}

export function useSidebarPreference(tenantSlug: string, preset: string) {
  const isDesktop = useSyncExternalStore(
    subscribeToDesktopBreakpoint,
    isDesktopViewport,
    () => false,
  );
  const storageKey = useMemo(
    () => sidebarPreferenceKey(tenantSlug, preset),
    [preset, tenantSlug],
  );
  const legacyKeys = useMemo(
    () => legacyPreferenceKeys(tenantSlug, preset),
    [preset, tenantSlug],
  );
  const [volatilePreferences, setVolatilePreferences] = useState<
    Record<string, boolean>
  >({});
  const [transitionsEnabled, setTransitionsEnabled] = useState(false);

  const storedPreference = readPreference(storageKey, legacyKeys);
  const volatilePreference = volatilePreferences[storageKey];
  const collapsed =
    volatilePreference ?? storedPreference ?? (isDesktop ? false : true);
  const hasPreference =
    volatilePreference !== undefined || storedPreference !== null;

  const toggleCollapsed = useCallback(() => {
    const next = !collapsed;
    setVolatilePreferences((current) => ({
      ...current,
      [storageKey]: next,
    }));
    try {
      const value: PersistedPreference = {
        version: PREFERENCE_VERSION,
        collapsed: next,
      };
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // Keep the in-memory choice when persistence is unavailable.
    }
  }, [collapsed, storageKey]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (
        event.key !== storageKey &&
        !legacyKeys.some((key) => key === event.key)
      ) {
        return;
      }
      const next = readPreference(storageKey, legacyKeys);
      setVolatilePreferences((current) => {
        if (next !== null) return { ...current, [storageKey]: next };
        const remaining = { ...current };
        delete remaining[storageKey];
        return remaining;
      });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [legacyKeys, storageKey]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() =>
      setTransitionsEnabled(true),
    );
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return {
    collapsed,
    hasPreference,
    isDesktop,
    storageKey,
    toggleCollapsed,
    transitionsEnabled,
  };
}
